import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { api } from "../services/apiClient";
import { executeRecaptcha } from "../services/recaptcha";
import { 
  Sparkle, List, ArrowRight, PlayCircle, MagicWand, Heart, 
  VideoCamera, Sun, TShirt, Camera, BookOpen, FilmStrip, Lightning, 
  Brain, Scissors, PaperPlaneTilt, Users, TiktokLogo, InstagramLogo, TwitterLogo,
  SpeakerHigh, SpeakerSlash
} from "@phosphor-icons/react";

gsap.registerPlugin(ScrollTrigger);

const THEME_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300..800;1,300..800&display=swap');
  @import url('https://api.fontshare.com/v2/css?f[]=clash-display@200,400,700,500,600,300&display=swap');

  :root {
    --brand-primary: #FF2E93;
    --brand-secondary: #FF8A00;
    --brand-tertiary: #00F0FF;
    --brand-purple: #8B5CF6;
    --bg-light: #F8F9FA;
    --text-dark: #0A0A0A;
    --glass-bg: rgba(255, 255, 255, 0.75);
    --glass-border: rgba(255, 255, 255, 0.6);
  }

  body {
    font-family: 'Plus Jakarta Sans', sans-serif;
    background-color: var(--bg-light);
    color: var(--text-dark);
    overflow-x: hidden;
    scroll-behavior: smooth;
    margin: 0;
  }

  h1, h2, h3, h4, .display-font {
    font-family: 'Clash Display', sans-serif;
  }

  /* Fluid, breathing gradient background */
  .holographic-bg {
    position: fixed;
    top: 0; left: 0; width: 100vw; height: 100vh;
    z-index: -2;
    background: 
      radial-gradient(circle at 10% 20%, rgba(255, 46, 147, 0.12) 0%, transparent 40%),
      radial-gradient(circle at 90% 80%, rgba(255, 138, 0, 0.12) 0%, transparent 40%),
      radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 20% 80%, rgba(0, 240, 255, 0.08) 0%, transparent 40%);
    filter: blur(60px);
    animation: breathe 25s ease-in-out infinite alternate;
  }

  @keyframes breathe {
    0% { transform: scale(1) translate(0px, 0px); }
    50% { transform: scale(1.1) translate(30px, -30px); }
    100% { transform: scale(0.95) translate(-20px, 40px); }
  }

  .noise {
    position: fixed;
    top: 0; left: 0; width: 100vw; height: 100vh;
    pointer-events: none; z-index: -1; opacity: 0.04;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
  }

  .glass-panel {
    background: var(--glass-bg);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border: 1px solid var(--glass-border);
    box-shadow: 
      0 4px 6px -1px rgba(0, 0, 0, 0.02), 
      0 10px 15px -3px rgba(0, 0, 0, 0.03),
      inset 0 1px 0 rgba(255,255,255,0.8);
  }

  .ui-card {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255,255,255,1);
    border-radius: 20px;
    box-shadow: 
      0 25px 50px -12px rgba(0,0,0,0.1),
      0 0 0 1px rgba(0,0,0,0.02);
    transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }
  
  .ui-card:hover {
    transform: scale(1.05) translateY(-5px);
    z-index: 50;
  }

  .creator-polaroid {
    background: white;
    padding: 8px;
    padding-bottom: 24px;
    border-radius: 12px;
    box-shadow: 0 20px 40px -10px rgba(0,0,0,0.15);
    border: 1px solid rgba(0,0,0,0.05);
    transition: all 0.3s ease;
  }
  
  .creator-polaroid:hover {
    transform: scale(1.08) rotate(0deg) !important;
    z-index: 40;
    box-shadow: 0 30px 60px -15px rgba(255, 46, 147, 0.3);
  }

  .marquee-wrapper {
    overflow: hidden;
    position: relative;
    padding: 1.5rem 0;
    background: var(--brand-purple);
    color: white;
    transform: rotate(-1.5deg) scale(1.02);
    z-index: 10;
  }
  
  .marquee-content {
    display: inline-flex;
    white-space: nowrap;
    animation: slideLeft 25s linear infinite;
  }
  
  @keyframes slideLeft {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }

  .text-gradient-animate {
    background: linear-gradient(to right, var(--brand-primary), var(--brand-secondary), var(--brand-purple), var(--brand-primary));
    background-size: 300% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: gradient-shift 6s ease-in-out infinite;
  }

  @keyframes gradient-shift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  .btn-glow {
    position: relative;
    transition: all 0.3s ease;
    overflow: hidden;
  }
  .btn-glow::before {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 100%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s ease;
  }
  .btn-glow:hover::before {
    left: 100%;
  }
  .btn-glow:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px -5px rgba(255, 46, 147, 0.4);
  }

  .phone-container {
    border-radius: 3rem;
    overflow: hidden;
    background: #000;
    box-shadow: 
      0 40px 80px -20px rgba(0,0,0,0.4),
      0 0 0 10px rgba(255,255,255,0.8),
      0 0 0 11px rgba(0,0,0,0.1);
    position: relative;
  }
  .phone-container::after {
    content: '';
    position: absolute;
    top: 15px; left: 50%;
    transform: translateX(-50%);
    width: 100px; height: 30px;
    background: #000;
    border-radius: 20px;
    z-index: 20;
  }
`;

export default function LandingPage() {
  const sceneRef = useRef(null);
  const navbarRef = useRef(null);
  const minoVideoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  // FIX: hamburger button existed with no state/handler at all — mobile
  // users had no way to reach Login or any nav link, since the real nav
  // is hidden entirely below the md breakpoint.
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Waitlist form state — this form previously had no state, no submit
  // handler, and was missing the `name` field the backend requires
  // (waitlistRoutes.js: `if (!name || !email || !recaptchaToken)`), so
  // clicking "Get Access" did nothing at all, silently.
  const [waitlistName, setWaitlistName] = useState('');
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistStatus, setWaitlistStatus] = useState('idle'); // idle | submitting | success | error
  const [waitlistError, setWaitlistError] = useState('');

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistName.trim() || !waitlistEmail.trim()) {
      setWaitlistStatus('error');
      setWaitlistError('Please enter your name and email.');
      return;
    }

    setWaitlistStatus('submitting');
    setWaitlistError('');

    try {
      // Real reCAPTCHA instead of the 'landing_bypass' token — this page
      // is the most public, highest-traffic unauthenticated surface in
      // the app, so it's the one place bot protection matters most, not
      // least. Matches the same executeRecaptcha pattern already proven
      // working in Signup.js and components/Waitlist.jsx.
      const recaptchaToken = await executeRecaptcha('WAITLIST');
      if (!recaptchaToken) {
        setWaitlistStatus('error');
        setWaitlistError('Security check failed. Please refresh and try again.');
        return;
      }

      const data = await api.post('/waitlist/join', {
        name: waitlistName.trim(),
        email: waitlistEmail.trim().toLowerCase(),
        recaptchaToken,
        source: 'website',
      });

      if (data.success) {
        setWaitlistStatus('success');
      } else {
        setWaitlistStatus('error');
        setWaitlistError(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setWaitlistStatus('error');
      setWaitlistError('Network error. Please check your connection and try again.');
    }
  };

  // Video Hover Logic
  const handleMinoHover = () => {
    if (minoVideoRef.current) {
      minoVideoRef.current.muted = false;
      setIsMuted(false);
    }
  };

  const handleMinoLeave = () => {
    if (minoVideoRef.current) {
      minoVideoRef.current.muted = true;
      setIsMuted(true);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (minoVideoRef.current) {
      const nextMuted = !minoVideoRef.current.muted;
      minoVideoRef.current.muted = nextMuted;
      setIsMuted(nextMuted);
    }
  };

  useEffect(() => {
    // Navbar Blur effect on scroll
    const handleScroll = () => {
      if (window.scrollY > 50) {
        navbarRef.current.classList.add("py-2", "backdrop-blur-md", "bg-white/50");
        navbarRef.current.classList.remove("py-4");
      } else {
        navbarRef.current.classList.add("py-4");
        navbarRef.current.classList.remove("py-2", "backdrop-blur-md", "bg-white/50");
      }
    };
    window.addEventListener("scroll", handleScroll);

    // Kinetic Hero Text Reveal
    gsap.fromTo(".hero-line", 
      { y: 120, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1.2,
        stagger: 0.15,
        ease: "power4.out",
        delay: 0.2
      }
    );

    // Dynamic Word Cycler
    const words = document.querySelectorAll('.word-cycle-item');
    let currentWord = 0;
    
    if (words.length > 0) {
      gsap.set(words[0], { opacity: 1, y: 0 });

      const cycleInterval = setInterval(() => {
        const nextWord = (currentWord + 1) % words.length;
        
        gsap.to(words[currentWord], {
          y: -40,
          opacity: 0,
          duration: 0.6,
          ease: "power2.inOut"
        });
        
        gsap.fromTo(words[nextWord], 
          { y: 40, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: "power2.inOut", delay: 0.15 }
        );
        
        currentWord = nextWord;
      }, 3000); 

      return () => clearInterval(cycleInterval);
    }

    // Standard Reveal (Fade + Slide up)
    gsap.utils.toArray(".gs-reveal").forEach((elem) => {
      gsap.fromTo(elem, 
        { y: 40, opacity: 0 }, 
        {
          y: 0, 
          opacity: 1, 
          duration: 1, 
          ease: "power3.out",
          scrollTrigger: {
            trigger: elem,
            start: "top 85%", 
          }
        }
      );
    });

    // Bouncy Pop-up Animation for Features & Creators
    gsap.utils.toArray(".gs-pop-up").forEach((elem) => {
      gsap.fromTo(elem,
        { y: 80, scale: 0.8, opacity: 0 },
        {
          y: 0, scale: 1, opacity: 1, duration: 1.2, ease: "back.out(1.5)",
          scrollTrigger: {
            trigger: elem,
            start: "top 85%",
          }
        }
      );
    });

    // Interactive Mouse Parallax (Hero Scene)
    const scene = sceneRef.current;
    const parallaxLayers = document.querySelectorAll('.parallax-layer');

    const onMouseMove = (e) => {
      const x = (e.clientX - window.innerWidth / 2);
      const y = (e.clientY - window.innerHeight / 2);

      parallaxLayers.forEach((layer) => {
        const speed = layer.getAttribute('data-speed');
        gsap.to(layer, {
          x: x * speed,
          y: y * speed,
          duration: 0.5,
          ease: "power1.out"
        });
      });
    };

    const onMouseLeave = () => {
      parallaxLayers.forEach((layer) => {
        gsap.to(layer, { x: 0, y: 0, duration: 1, ease: "power2.out" });
      });
    };

    if (scene && parallaxLayers.length > 0) {
      scene.addEventListener('mousemove', onMouseMove);
      scene.addEventListener('mouseleave', onMouseLeave);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scene) {
        scene.removeEventListener('mousemove', onMouseMove);
        scene.removeEventListener('mouseleave', onMouseLeave);
      }
      // FIX: ScrollTrigger instances created above (.gs-reveal, .gs-pop-up) were
      // never killed on unmount. They live outside React's lifecycle, so
      // navigating away from the landing page left them registered against
      // removed DOM elements — inflating the scroll height of whatever page
      // you navigated to next (e.g. /about showed a huge blank scroll area).
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <div className="antialiased selection:bg-[#FF2E93] selection:text-white relative">
      <style>{THEME_CSS}</style>

      <div className="holographic-bg"></div>
      <div className="noise"></div>

      {/* Navbar */}
      <nav ref={navbarRef} className="fixed w-full z-50 top-0 px-4 py-4 transition-all duration-300">
        <div className="max-w-7xl mx-auto glass-panel rounded-full px-4 py-2.5 flex justify-between items-center shadow-sm border-white/80">
          
          {/* Sized Up Navbar Logo - Removed mix-blend-multiply */}
          <a href="/" className="bg-white rounded-full px-4 py-1.5 flex items-center shadow-sm border border-gray-100 hover:scale-105 transition-transform">
            <img src="/Infleuince_Landing.png" alt="Infleuince" className="h-10 md:h-14 w-auto object-contain" />
          </a>
          
          <div className="hidden md:flex items-center gap-10 font-bold text-sm tracking-wide text-gray-700">
            <a href="#vibe" className="hover:text-[#FF2E93] transition-colors">The Vibe</a>
            <a href="#mino" className="hover:text-[#8B5CF6] transition-colors">Meet Mino</a>
            <a href="#features" className="hover:text-[#FF8A00] transition-colors">Platform</a>
            <a href="#creators" className="hover:text-[#00F0FF] transition-colors">Creators</a>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <a href="/login" className="font-bold text-sm text-gray-600 hover:text-black transition-colors">Log In</a>
            <a href="#waitlist" className="btn-glow bg-black text-white px-7 py-2.5 rounded-full font-bold text-sm flex items-center gap-2 shadow-lg">
              Get Access <Sparkle weight="bold" className="text-[#FF2E93]" />
            </a>
          </div>
          
          <button
            className="md:hidden text-2xl w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm"
            onClick={() => setMobileMenuOpen(o => !o)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <List weight="bold" />
          </button>
        </div>

        {/* Mobile dropdown — everything that's hidden on desktop's inline
            nav (md:flex) needs to live here instead, or it's unreachable
            on mobile entirely. */}
        {mobileMenuOpen && (
          <div className="md:hidden max-w-7xl mx-auto mt-2 glass-panel rounded-3xl px-6 py-5 flex flex-col gap-4 font-bold text-sm text-gray-700 shadow-sm border-white/80">
            <a href="#vibe" onClick={() => setMobileMenuOpen(false)} className="hover:text-[#FF2E93] transition-colors">The Vibe</a>
            <a href="#mino" onClick={() => setMobileMenuOpen(false)} className="hover:text-[#8B5CF6] transition-colors">Meet Mino</a>
            <a href="#features" onClick={() => setMobileMenuOpen(false)} className="hover:text-[#FF8A00] transition-colors">Platform</a>
            <a href="#creators" onClick={() => setMobileMenuOpen(false)} className="hover:text-[#00F0FF] transition-colors">Creators</a>
            <div className="border-t border-gray-100 pt-4 flex flex-col gap-3">
              <a href="/login" className="text-gray-600 hover:text-black transition-colors">Log In</a>
              <a href="#waitlist" onClick={() => setMobileMenuOpen(false)} className="btn-glow bg-black text-white px-7 py-2.5 rounded-full text-center flex items-center justify-center gap-2 shadow-lg">
                Get Access <Sparkle weight="bold" className="text-[#FF2E93]" />
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section ref={sceneRef} className="relative pt-36 pb-24 min-h-[95vh] flex items-center overflow-hidden" id="hero-scene">
        <div className="max-w-7xl mx-auto px-6 relative z-10 grid lg:grid-cols-12 gap-12 items-center w-full">
          
          <div className="lg:col-span-6 relative z-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur border border-white shadow-sm text-xs font-bold mb-8 text-[#8B5CF6] uppercase tracking-widest gs-reveal">
              <img src="/infleuince_camera_icon.jpg" className="w-4 h-4 rounded-full" alt="icon" />
              The AI Co-Creator is Live
            </div>
            
            <h1 className="text-[3.5rem] md:text-7xl lg:text-[5rem] font-semibold display-font leading-[1.05] tracking-tight mb-6 relative z-20">
              <div className="overflow-hidden py-1"><span className="block hero-line">CREATE <span className="text-gradient-animate">MORE.</span></span></div>
              <div className="overflow-hidden py-1"><span className="block hero-line italic font-light text-gray-500">EDIT SMARTER.</span></div>
              <div className="overflow-hidden h-[70px] md:h-[90px] mt-2 py-1 relative w-full block min-w-[320px]">
                <span className="block hero-line font-bold text-gray-900">
                  <span className="word-cycle-item absolute top-0 left-0">HAVE FUN.</span>
                  <span className="word-cycle-item absolute top-0 left-0 opacity-0">BE YOU.</span>
                  <span className="word-cycle-item absolute top-0 left-0 opacity-0">BUILD COMMUNITY.</span>
                </span>
              </div>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-lg font-medium leading-relaxed gs-reveal">
              Content creation should be fun. Drop in your raw footage, and we’ll instantly find the best hooks, auto-crop your clips, and write captions in your true voice—so you can just hit publish.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 gs-reveal">
              <a href="#waitlist" className="btn-glow bg-black text-white px-8 py-4 rounded-full font-bold text-lg flex items-center justify-center gap-2 shadow-2xl shadow-black/20">
                Join the Community <ArrowRight weight="bold" />
              </a>
              <button className="px-8 py-4 rounded-full font-bold text-lg border-2 border-gray-900/10 hover:bg-white/60 transition-colors flex items-center justify-center gap-2 glass-panel group">
                <PlayCircle weight="fill" className="text-2xl text-gray-800 group-hover:text-[#FF2E93] transition-colors" /> See it in action
              </button>
            </div>
          </div>

          <div className="lg:col-span-6 relative h-[700px] w-full hidden md:block" style={{ perspective: '1000px' }}>
            
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px] h-[680px] phone-container z-20 parallax-layer" data-speed="0.02">
              <video autoPlay loop muted playsInline className="w-full h-full object-cover" poster="https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?auto=format&fit=crop&w=400&q=80">
                <source src="filming-content.mp4" type="video/mp4" />
              </video>
              
              <div className="absolute bottom-10 left-1/2 -translate-x-1/2 w-[85%] bg-white/20 backdrop-blur-xl border border-white/40 p-4 rounded-3xl shadow-2xl flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-tr from-[#FF2E93] to-[#8B5CF6] rounded-full flex items-center justify-center shrink-0 shadow-inner">
                  <MagicWand weight="bold" className="text-white text-xl" />
                </div>
                <div className="text-white text-left">
                  <p className="font-bold text-sm leading-tight">Engagement Hook Found</p>
                  <p className="text-[10px] opacity-80 mt-0.5">Auto-cropped & captioned</p>
                </div>
              </div>
            </div>

            <div className="absolute top-[10%] left-[-10%] creator-polaroid z-30 parallax-layer" data-speed="0.06" style={{ transform: "rotate(-12deg)" }}>
              <img src="/Aesthetic-Creator.png" 
                   className="w-32 h-32 object-cover rounded-lg" alt="Aesthetic Creator" />
            </div>
            
            <div className="absolute top-[25%] right-[-5%] creator-polaroid z-10 parallax-layer" data-speed="0.04" style={{ transform: "rotate(8deg)" }}>
              <img src="https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&w=300&h=350&q=80" 
                   className="w-28 h-36 object-cover rounded-lg" alt="Fashion Creator" />
            </div>

            <div className="absolute bottom-[20%] left-[0%] creator-polaroid z-30 parallax-layer" data-speed="0.05" style={{ transform: "rotate(-6deg)" }}>
              <img src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=200&h=200&q=80" 
                   className="w-24 h-24 object-cover rounded-lg" alt="Lifestyle Vlogger" />
            </div>

            <div className="absolute top-[45%] right-[-15%] ui-card p-4 flex flex-col items-center gap-2 z-40 parallax-layer" data-speed="0.08" style={{ transform: "rotate(4deg)", width: "130px" }}>
              <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center">
                <Heart weight="fill" className="text-[#FF2E93] text-2xl" />
              </div>
              <div className="text-center">
                <span className="block font-bold text-2xl display-font">284K</span>
                <span className="block text-[10px] text-gray-500 font-bold uppercase tracking-wider">Active Community</span>
              </div>
            </div>
            
            <div className="absolute top-1/3 left-1/4 w-40 h-40 bg-[#FF2E93] rounded-full mix-blend-multiply blur-[70px] opacity-30 parallax-layer" data-speed="0.01"></div>
            <div className="absolute bottom-1/3 right-1/4 w-48 h-48 bg-[#8B5CF6] rounded-full mix-blend-multiply blur-[80px] opacity-30 parallax-layer" data-speed="0.02"></div>
          </div>
        </div>
      </section>

      {/* Marquee Section */}
      <div className="marquee-wrapper shadow-2xl border-y border-white/20">
        <div className="marquee-content inline-flex flex-row whitespace-nowrap display-font text-3xl font-bold uppercase tracking-wider gap-8 items-center opacity-90">
          <span className="flex items-center gap-2"><VideoCamera weight="bold" /> GRWM</span> <span className="text-white/40">•</span>
          <span className="flex items-center gap-2"><Sun weight="bold" /> DAY IN THE LIFE</span> <span className="text-white/40">•</span>
          <span className="flex items-center gap-2"><TShirt weight="bold" /> OOTD</span> <span className="text-white/40">•</span>
          <span className="flex items-center gap-2"><Camera weight="bold" /> VLOGS</span> <span className="text-white/40">•</span>
          <span className="flex items-center gap-2"><BookOpen weight="bold" /> STORYTIME</span> <span className="text-white/40">•</span>
          <span className="flex items-center gap-2"><FilmStrip weight="bold" /> MINI VLOG</span> <span className="text-white/40">•</span>
          <span className="text-[#00F0FF] flex items-center gap-2"><Lightning weight="bold" /> AI CO-CREATOR</span> <span className="text-white/40">•</span>
          
          <span className="flex items-center gap-2"><VideoCamera weight="bold" /> GRWM</span> <span className="text-white/40">•</span>
          <span className="flex items-center gap-2"><Sun weight="bold" /> DAY IN THE LIFE</span> <span className="text-white/40">•</span>
          <span className="flex items-center gap-2"><TShirt weight="bold" /> OOTD</span> <span className="text-white/40">•</span>
          <span className="flex items-center gap-2"><Camera weight="bold" /> VLOGS</span> <span className="text-white/40">•</span>
          <span className="flex items-center gap-2"><BookOpen weight="bold" /> STORYTIME</span> <span className="text-white/40">•</span>
          <span className="flex items-center gap-2"><FilmStrip weight="bold" /> MINI VLOG</span> <span className="text-white/40">•</span>
          <span className="text-[#00F0FF] flex items-center gap-2"><Lightning weight="bold" /> AI CO-CREATOR</span> <span className="text-white/40">•</span>
        </div>
      </div>

      {/* Meet Mino Section */}
      <section id="mino" className="py-32 relative px-4 overflow-hidden bg-white">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-[#8B5CF6]/10 to-transparent rounded-full blur-[100px]"></div>
        
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          
          <div className="relative gs-reveal">
            <div className="absolute inset-0 bg-gradient-to-tr from-[#8B5CF6] to-[#FF2E93] opacity-20 blur-3xl rounded-[40%_60%_70%_30%/40%_50%_60%_50%] animate-[morph_8s_ease-in-out_infinite]"></div>
            
            {/* Interactive Video Card */}
            <div 
              className="relative z-10 w-full max-w-md mx-auto ui-card p-4 pb-8 transform rotate-2 shadow-2xl transition-transform hover:scale-105 cursor-pointer"
              onMouseEnter={handleMinoHover}
              onMouseLeave={handleMinoLeave}
              onClick={toggleMute}
            >
              <div className="relative">
                <video 
                  ref={minoVideoRef}
                  autoPlay 
                  loop 
                  muted 
                  playsInline 
                  className="w-full h-auto rounded-xl shadow-inner mb-6" 
                  poster="/IMG_E7667.jpg"
                >
                   <source src="/Rollerskating-Mino.mp4" type="video/mp4" />
                </video>
                
                {/* Audio Indicator Overlay */}
                <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-md rounded-full p-2 text-white transition-opacity">
                  {isMuted ? <SpeakerSlash weight="bold" /> : <SpeakerHigh weight="bold" />}
                </div>
              </div>
              
              <div className="absolute -right-8 -bottom-8 bg-white p-5 rounded-2xl rounded-tl-none shadow-xl border border-gray-100 max-w-[250px] transform -rotate-2">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Mino • Your Biggest Fan</span>
                </div>
                <p className="text-sm font-medium text-gray-800 leading-tight">"Trying to manage your content pipeline while actually living your life? Just go outside and enjoy the sunshine. Let me handle the edit."</p>
              </div>
            </div>
          </div>

          <div className="gs-reveal">
            <h2 className="text-5xl md:text-6xl font-bold display-font mb-6 tracking-tight leading-tight">MEET MINO.<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B5CF6] to-[#FF2E93]">YOUR BIGGEST FAN.</span></h2>
            <p className="text-xl text-gray-600 mb-8 font-medium leading-relaxed">
              When do we actually get to enjoy the sunshine? Mino is the mascot of the Infleuince platform. While the engine watches your raw footage, finds the exact moments your audience will love, and edits it perfectly, Mino is here to remind you to step away from the screen and actually live your life. 
            </p>
            
            <ul className="space-y-6">
              <li className="flex items-center gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center shrink-0 text-[#8B5CF6]">
                  <Brain weight="fill" className="text-2xl" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900">Audience Psychology Built-in</h4>
                  <p className="text-sm text-gray-500 font-medium">The engine knows what creates genuine community connection.</p>
                </div>
              </li>
              <li className="flex items-center gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center shrink-0 text-[#FF2E93]">
                  <MagicWand weight="fill" className="text-2xl" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900">Zero Manual Editing</h4>
                  <p className="text-sm text-gray-500 font-medium">Trims the dead air and keeps you centered automatically.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 relative px-4 bg-gray-50/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-24 gs-reveal">
            <h2 className="text-5xl md:text-6xl font-bold display-font mb-6 tracking-tight">TECH THAT GETS <br/><span className="italic text-gray-400 font-light">YOUR AESTHETIC.</span></h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto font-medium">We combined cinematic AI editing with real audience psychology. You just hit record. We handle the rest.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" style={{ perspective: '1000px' }}>
            
            <div className="ui-card p-10 rounded-[2.5rem] gs-pop-up relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#00F0FF] opacity-5 blur-[80px] rounded-full group-hover:opacity-20 transition-opacity duration-700"></div>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-cyan-100 to-blue-50 flex items-center justify-center mb-8 border border-white shadow-sm relative z-10">
                <Sparkle weight="bold" className="text-3xl text-[#00F0FF]" />
              </div>
              <h3 className="text-2xl font-bold display-font mb-4 relative z-10">Vibe Check AI</h3>
              <p className="text-gray-600 font-medium leading-relaxed relative z-10">
                Drop your handle. Our <strong>Brand Scanner</strong> instantly analyzes your color palette, tone, and content pillars to make sure everything we generate sounds exactly like you.
              </p>
            </div>

            <div className="ui-card p-10 rounded-[2.5rem] gs-pop-up relative overflow-hidden group mt-0 lg:mt-12">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#FF2E93] opacity-5 blur-[80px] rounded-full group-hover:opacity-20 transition-opacity duration-700"></div>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-pink-100 to-rose-50 flex items-center justify-center mb-8 border border-white shadow-sm relative z-10">
                <Scissors weight="bold" className="text-3xl text-[#FF2E93]" />
              </div>
              <h3 className="text-2xl font-bold display-font mb-4 relative z-10">The Engagement Editor</h3>
              <p className="text-gray-600 font-medium leading-relaxed relative z-10">
                Upload a 10-minute vlog. Our <strong>AI Director</strong> analyzes the transcript, finds the juiciest 15-second hook for your community, auto-crops to 9:16, and trims the silence.
              </p>
            </div>

            <div className="ui-card p-10 rounded-[2.5rem] gs-pop-up relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#8B5CF6] opacity-5 blur-[80px] rounded-full group-hover:opacity-20 transition-opacity duration-700"></div>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-purple-100 to-indigo-50 flex items-center justify-center mb-8 border border-white shadow-sm relative z-10">
                <PaperPlaneTilt weight="bold" className="text-3xl text-[#8B5CF6]" />
              </div>
              <h3 className="text-2xl font-bold display-font mb-4 relative z-10">One-Click Drop</h3>
              <p className="text-gray-600 font-medium leading-relaxed relative z-10">
                Why post manually? We generate 3 psychologically-tested caption variants and push the final render to TikTok and IG simultaneously through official APIs.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Creators Section */}
      <section id="creators" className="py-32 px-4 relative bg-white">
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-50 border border-pink-100 text-xs font-bold mb-6 text-[#FF2E93] uppercase tracking-widest gs-reveal">
            <Users weight="fill" /> The Community
          </div>
          <h2 className="text-5xl md:text-6xl font-bold display-font mb-6 tracking-tight gs-reveal">CREATORS WHO<br/>GOT THEIR TIME BACK.</h2>
          <p className="text-gray-500 mb-20 font-medium text-xl max-w-2xl mx-auto gs-reveal">Join the movement of lifestyle creators who stopped fighting timelines and started connecting authentically.</p>
          
          <div className="grid md:grid-cols-3 gap-8 text-left">
            <div className="glass-panel p-8 rounded-[2rem] ui-card gs-pop-up" style={{ transform: "rotate(-1deg)" }}>
              <div className="w-full h-48 rounded-xl bg-gray-200 mb-6 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=600&q=80" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" alt="Creator" />
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white shrink-0">
                  <TiktokLogo weight="fill" />
                </div>
                <div>
                  <h4 className="font-bold display-font text-lg leading-tight">Chloe T.</h4>
                  <p className="text-[11px] text-[#FF2E93] font-bold uppercase tracking-wider">Lifestyle Vlogger</p>
                </div>
              </div>
              <p className="text-sm font-medium text-gray-600 leading-relaxed">"I literally upload my 20 min morning routines, and the engine chops out the best community highlights for TikTok. It matched my sarcastic caption style perfectly on day 1."</p>
            </div>
            
            <div className="glass-panel p-8 rounded-[2rem] ui-card gs-pop-up md:mt-12" style={{ transform: "rotate(1deg)" }}>
              <div className="w-full h-48 rounded-xl bg-gray-200 mb-6 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=600&q=80" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" alt="Creator" />
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-500 flex items-center justify-center text-white shrink-0">
                  <InstagramLogo weight="fill" />
                </div>
                <div>
                  <h4 className="font-bold display-font text-lg leading-tight">Elena R.</h4>
                  <p className="text-[11px] text-[#FF8A00] font-bold uppercase tracking-wider">Fashion / OOTD</p>
                </div>
              </div>
              <p className="text-sm font-medium text-gray-600 leading-relaxed">"The Smart Crop for Reels is witchcraft. It tracks my face even when I'm walking around my studio setting up outfits. Saved me 4 hours this week alone."</p>
            </div>

            <div className="glass-panel p-8 rounded-[2rem] ui-card gs-pop-up" style={{ transform: "rotate(-0.5deg)" }}>
              <div className="w-full h-48 rounded-xl bg-gray-200 mb-6 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=600&q=80" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" alt="Creator" />
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center text-white shrink-0">
                  <TwitterLogo weight="fill" />
                </div>
                <div>
                  <h4 className="font-bold display-font text-lg leading-tight">Maya S.</h4>
                  <p className="text-[11px] text-[#8B5CF6] font-bold uppercase tracking-wider">Beauty & GRWM</p>
                </div>
              </div>
              <p className="text-sm font-medium text-gray-600 leading-relaxed">"I used the Brand Scanner on my IG, and now the AI captions it writes sound more like me than I do. Obsessed with the One-Click publish directly to my feeds."</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Footer Section */}
      <footer id="waitlist" className="mt-0 border-t border-gray-100 bg-white relative overflow-hidden">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-64 bg-[radial-gradient(ellipse_at_bottom,rgba(139,92,246,0.15),transparent_70%)] pointer-events-none"></div>
        
        <div className="max-w-4xl mx-auto px-6 py-32 text-center relative z-10">
          <div className="w-24 h-24 mx-auto bg-purple-50 rounded-full flex items-center justify-center mb-8 shadow-inner border border-purple-100">
            <img src="/infleuince_camera_icon.jpg" className="w-12 h-12" alt="Infleuince Icon" />
          </div>
          <h2 className="text-5xl md:text-7xl font-bold display-font mb-6 tracking-tight">READY TO CONNECT?</h2>
          <p className="text-xl text-gray-500 mb-12 font-medium">Stop wasting hours editing on your phone. Get your time back.</p>
          
          {waitlistStatus === 'success' ? (
            <div className="max-w-md mx-auto bg-green-50 border border-green-200 rounded-2xl px-6 py-5 text-green-800 font-medium">
              You're on the list! Check your email for confirmation.
            </div>
          ) : (
            <form onSubmit={handleWaitlistSubmit} className="flex flex-col gap-3 max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  placeholder="Your name"
                  value={waitlistName}
                  onChange={(e) => setWaitlistName(e.target.value)}
                  disabled={waitlistStatus === 'submitting'}
                  className="flex-1 bg-gray-50 border border-gray-200 rounded-full px-6 py-4 outline-none focus:border-[#8B5CF6] focus:bg-white transition-all font-medium text-center sm:text-left shadow-inner disabled:opacity-60"
                />
                <input
                  type="email"
                  placeholder="Drop your email..."
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  disabled={waitlistStatus === 'submitting'}
                  className="flex-1 bg-gray-50 border border-gray-200 rounded-full px-6 py-4 outline-none focus:border-[#8B5CF6] focus:bg-white transition-all font-medium text-center sm:text-left shadow-inner disabled:opacity-60"
                />
              </div>
              <button
                type="submit"
                disabled={waitlistStatus === 'submitting'}
                className="btn-glow bg-black text-white font-bold px-8 py-4 rounded-full whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60 w-full"
              >
                {waitlistStatus === 'submitting' ? 'Joining...' : 'Get Access'} <ArrowRight weight="bold" />
              </button>
              {waitlistStatus === 'error' && (
                <p className="text-red-600 text-sm font-medium">{waitlistError}</p>
              )}
            </form>
          )}
        </div>

        {/* Real Footer Links */}
        <div className="max-w-6xl mx-auto px-10 py-12 border-t border-gray-100 flex flex-col md:flex-row justify-between gap-12 relative z-10">
          <div className="max-w-xs">
            {/* Sized Up Footer Logo - Removed mix-blend-multiply */}
            <img src="/Infleuince_Landing.png" className="h-16 md:h-24 mb-4 w-auto object-contain" alt="Infleuince Logo" />
            <p className="text-gray-500 text-sm font-medium">The complete operating system for creators. Stop formatting, start connecting, and get your time back.</p>
          </div>
          
          <div className="flex flex-wrap gap-16">
            <div className="flex flex-col gap-3">
              <span className="font-bold display-font text-gray-900 mb-2">Company</span>
              <a href="/about" className="text-gray-500 hover:text-[#8B5CF6] text-sm font-medium transition-colors">About Us</a>
              <a href="/contact-us" className="text-gray-500 hover:text-[#8B5CF6] text-sm font-medium transition-colors">Contact</a>
              <a href="/help-center" className="text-gray-500 hover:text-[#8B5CF6] text-sm font-medium transition-colors">Help Center</a>
            </div>
            
            <div className="flex flex-col gap-3">
              <span className="font-bold display-font text-gray-900 mb-2">Legal</span>
              <a href="/privacy-policy" className="text-gray-500 hover:text-[#8B5CF6] text-sm font-medium transition-colors">Privacy Policy</a>
              <a href="/terms-of-service" className="text-gray-500 hover:text-[#8B5CF6] text-sm font-medium transition-colors">Terms of Service</a>
            </div>

            <div className="flex flex-col gap-3">
              <span className="font-bold display-font text-gray-900 mb-2">Social</span>
              <a href="https://instagram.com/infleuince" target="_blank" rel="noreferrer" className="text-gray-500 hover:text-[#FF2E93] text-sm font-medium transition-colors flex items-center gap-2"><InstagramLogo weight="fill" /> Instagram</a>
              <a href="https://tiktok.com/@infleuince" target="_blank" rel="noreferrer" className="text-gray-500 hover:text-black text-sm font-medium transition-colors flex items-center gap-2"><TiktokLogo weight="fill" /> TikTok</a>
              <a href="https://linkedin.com/company/infleuince" target="_blank" rel="noreferrer" className="text-gray-500 hover:text-[#0077b5] text-sm font-medium transition-colors flex items-center gap-2"><TwitterLogo weight="fill" /> LinkedIn</a>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 py-6 text-center text-sm text-gray-400 font-medium relative z-10 px-10">
          <p>© {new Date().getFullYear()} Infleuince. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}